import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'reactstrap';

import { translate } from '../../commons/localize';
import { getLabelValueOptionsList, mapToOptionsList } from '../../commons/utils';

import SelectorDropDown from '../SelectorDropDown';
import ChildsSelector from '../ChildsSelector';

const defaultExtraData = { maxOccupancy: 1, minAge: 0, maxAge: 0, childrenAllowed: false };

const Room = (props) => {
  const [roomExtraData, setRoomExtraData] = useState(defaultExtraData);

  const {
    room,
    canBeRemoved,
    availableRoomsList,
    availableMealPlansList,
    readOnly,
    hasToValidate
  } = props;

  const {
    id,
    roomCode,
    boardCode,
    distribution
  } = room;

  useEffect(() => {
    if (roomCode === '') {
      setRoomExtraData(defaultExtraData);
      return;
    }

    const filteredData = availableRoomsList.find(availableRoom => availableRoom.code === roomCode);

    if (!filteredData) {
      setRoomExtraData(defaultExtraData);
      return;
    }

    checkIfHasToUpdate(filteredData.maxOccupancy);

    if (!filteredData.childrenAllowed) {
      const { children } = distribution;

      if (children !== 0) handleChildsDistribution(0, []);
    }

    setRoomExtraData(filteredData);
  }, [roomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const cehckValidation = (value) => {
    if (hasToValidate) return (value !== '');

    return true;
  };

  const checkIfHasToUpdate = (maxOccupancy) => {
    const { adults, children, ages } = distribution;
    let distributionData = {};

    if (maxOccupancy >= adults && maxOccupancy >= children) return;

    const updateAdults = (adults && adults > 1);
    const updateChildren = (children && children > 0);

    if (updateAdults) {
      const adultsToErase = adults - maxOccupancy;

      distributionData = {
        adults: (adults - adultsToErase)
      }
    }

    if (updateChildren) {
      const childsToErase = children - maxOccupancy;

      distributionData = {
        ...distributionData,
        children: (children - childsToErase),
        ages: ages.slice(0, (children - childsToErase))
      }
    }

    props.handleDistribution(id, distributionData);
  };

  const handleMealPlan = (mealPlan) => {
    props.handleMealPlan(id, mealPlan);
  };

  const handleRoomCode = (roomCode) => {
    props.handleRoomCode(id, roomCode);
  };

  const handleAdultsDistribution = (value) => {
    props.handleDistribution(id, { adults: value });
  };

  const handleChildsDistribution = (paxesNumber, agesList) => {
    props.handleDistribution(id, { children: paxesNumber, ages: agesList });
  };

  const deleteRoom = () => {
    props.handleEraseRoom(id);
  };

  const isBlocked = (roomCode === '');
  const customPaxesProps = (readOnly) ? { readOnly } : { isBlocked };

  return (
    <Row className="room-manager__container">
      <Col sm="4">
        <SelectorDropDown
          title={translate('tipo')}
          dataTestLabel="selector-habitacion"
          options={mapToOptionsList(availableRoomsList, 'name', 'code')}
          defaultValue={roomCode}
          checkPrevProps
          isValid={cehckValidation(roomCode)}
          readOnly={readOnly}
          handleChangeOnUpdate={true}
          handleSelectOption={handleRoomCode}
          placeHolder={translate('placeHolderSelector')}
        />
      </Col>
      <Col sm="3">
        <SelectorDropDown
          title={translate('regimen')}
          dataTestLabel="selector-regimen"
          options={mapToOptionsList(availableMealPlansList, 'description', 'code')}
          defaultValue={boardCode}
          checkPrevProps
          isValid={cehckValidation(boardCode)}
          readOnly={readOnly}
          handleChangeOnUpdate={true}
          handleSelectOption={handleMealPlan}
          placeHolder={translate('placeHolderSelector')}
        />
      </Col>
      <Col sm="1" className="no-padding-right">
        <SelectorDropDown
          title={translate('adultos')}
          dataTestLabel="selector-adultos"
          checkPrevProps
          options={getLabelValueOptionsList(roomExtraData.maxOccupancy, false)}
          defaultValue={distribution.adults}
          isSearchable={false}
          {...customPaxesProps}
          handleSelectOption={handleAdultsDistribution}
        />
      </Col>
      {(roomExtraData.childrenAllowed) &&
        <Col sm="2" className="no-padding-right">
          <ChildsSelector
            dataTestLabel="selector-ninos"
            childsValue={distribution.children}
            childsAges={distribution.ages}
            maxOccupancy={roomExtraData.maxOccupancy}
            minAge={roomExtraData.minAge}
            maxAge={roomExtraData.maxAge}
            {...customPaxesProps}
            handleSelectOption={handleChildsDistribution}
          />
        </Col>
      }
      {
        (!readOnly && canBeRemoved) &&
        <Col sm="auto">
          <span className="delete-icon">
            <Button
              className="icon-button"
              data-test-id="boton-eliminar-reserva"
              onClick={deleteRoom}
            >
              <span className="icon-eliminar" />
            </Button>
          </span>
        </Col>
      }
    </Row>
  );
};

Room.propTypes = {
  room: PropTypes.shape({
    id: PropTypes.number,
    roomCode: PropTypes.string,
    boardCode: PropTypes.string,
    distribution: PropTypes.shape({
      adults: PropTypes.number,
      children: PropTypes.number
    })
  }).isRequired,
  canBeRemoved: PropTypes.bool,
  availableRoomsList: PropTypes.arrayOf(PropTypes.object),
  availableMealPlansList: PropTypes.arrayOf(PropTypes.object),
  readOnly: PropTypes.bool,
  hasToValidate: PropTypes.bool,
  handleRoomCode: PropTypes.func,
  handleMealPlan: PropTypes.func,
  handleDistribution: PropTypes.func,
  handleEraseRoom: PropTypes.func
};

Room.defaultProps = {
  canBeRemoved: false,
  availableRoomsList: [],
  availableMealPlansList: [],
  readOnly: false,
  hasToValidate: false,
  handleRoomCode: () => {},
  handleMealPlan: () => {},
  handleDistribution: () => {},
  handleEraseRoom: () => {}
};

export default Room;
